import { type RefObject, useLayoutEffect, useRef } from 'react'
import { type CrossClientQueryInputMode } from './CrossClientQueryInput'

const HEIGHT_ANIMATION_DURATION_MS = 280
const HEIGHT_ANIMATION_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)'

// The chat card is docked at the bottom and grows upward, so a mode switch would make the question jump.
// Animate the content from its last settled height to the new one instead.
export const useAnimatedHeight = (contentRef: RefObject<HTMLElement | null>, mode: CrossClientQueryInputMode) => {
  const lastHeightRef = useRef<number | null>(null)
  const animationRef = useRef<Animation | null>(null)
  const previousModeRef = useRef(mode)

  useLayoutEffect(() => {
    const content = contentRef.current
    if (!content || typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(() => {
      lastHeightRef.current = content.offsetHeight
    })
    observer.observe(content)
    return () => observer.disconnect()
  }, [contentRef])

  useLayoutEffect(() => {
    const content = contentRef.current
    const previousMode = previousModeRef.current
    previousModeRef.current = mode
    if (!content || lastHeightRef.current === null) {
      return
    }
    // If a previous animation is still running, start from where it visually is, then drop its height override
    // (and clip) so the new content's natural height can be measured.
    const runningAnimation = animationRef.current
    const previousHeight = runningAnimation ? content.getBoundingClientRect().height : lastHeightRef.current
    runningAnimation?.cancel()
    animationRef.current = null
    content.style.overflow = ''
    content.style.minHeight = ''
    // Re-running from an answer (e.g. "Use suggested") passes through a short working state on the way to the next
    // answer. Hold the answer's height meanwhile so the card makes one move instead of collapsing and re-opening.
    if (previousMode === 'review' && mode === 'working') {
      content.style.minHeight = `${previousHeight}px`
      return
    }
    if (typeof content.animate !== 'function') {
      return
    }
    const nextHeight = content.offsetHeight
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (previousHeight === nextHeight || prefersReducedMotion) {
      return
    }
    content.style.overflow = 'hidden'
    const animation = content.animate([{ height: `${previousHeight}px` }, { height: `${nextHeight}px` }], {
      duration: HEIGHT_ANIMATION_DURATION_MS,
      easing: HEIGHT_ANIMATION_EASING,
    })
    // An interrupted animation reports `cancel` asynchronously; only the latest one may clear the clip. Clearing the ref
    // too means the next mode change starts from the settled height rather than treating this one as still running.
    const cleanUp = () => {
      if (animationRef.current === animation) {
        animationRef.current = null
        content.style.overflow = ''
      }
    }
    animation.addEventListener('finish', cleanUp)
    animation.addEventListener('cancel', cleanUp)
    animationRef.current = animation
  }, [contentRef, mode])
}
